/**
 * Shared unwrap for Write-button structured JSON envelopes.
 * Kept free of agenticChat imports so client carousel sanitizers can use it safely.
 */

const SINGLE_PROSE_JSON_KEYS = [
  "replacement_prose",
  "prose",
  "text",
  "content",
  "manuscript_prose",
] as const;

const WRITE_PROSE_JSON_KEY_RE = /"(?:replacement_prose|prose|text|content|manuscript_prose)"\s*:/;

/** Strip markdown fences and leading chatter so model output can pass strict JSON parsing. */
export function normalizeWriteProseStructuredRaw(raw: string): string {
  let text = String(raw ?? "").trim();
  if (!text) return "";

  const fullyFenced = text.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/i);
  if (fullyFenced) {
    text = fullyFenced[1]!.trim();
  } else {
    if (text.startsWith("```json")) {
      text = text.slice(7).trimStart();
    } else if (text.startsWith("```")) {
      text = text.slice(3).trimStart();
    }
    if (text.endsWith("```")) {
      text = text.slice(0, -3).trimEnd();
    }
    text = text.trim();
  }

  if (text.startsWith("{") && text.endsWith("}")) return text;

  const braceStart = text.indexOf("{");
  const braceEnd = text.lastIndexOf("}");
  if (braceStart !== -1 && braceEnd > braceStart) {
    return text.slice(braceStart, braceEnd + 1);
  }

  return text;
}

/** Drop trailing "Summary of changes" / Opening-Ending notes from otherwise-valid chapter prose. */
export function stripTrailingChangeSummaryForWriteContract(prose: string): string {
  const text = String(prose ?? "").trim();
  if (!text) return "";
  const summarySplit = text.split(
    /\n(?:#{1,3}\s*)?(?:\*{0,2})?(?:summary\s+of\s+changes|key\s+changes(?:\s+made)?)\b[^\n]*\n/i,
  );
  if (summarySplit.length < 2) return text;
  const before = summarySplit[0]!.trim();
  if (before.split(/\s+/).filter(Boolean).length < 40) return text;
  return before;
}

/** True when text still looks like a structured Write JSON envelope (not manuscript prose). */
export function looksLikeWriteProseJsonEnvelope(raw: string): boolean {
  const text = String(raw ?? "").trim();
  if (!text.startsWith("{") || !WRITE_PROSE_JSON_KEY_RE.test(text)) return false;
  const normalized = normalizeWriteProseStructuredRaw(text);
  return normalized.startsWith("{") && WRITE_PROSE_JSON_KEY_RE.test(normalized);
}

function decodeJsonStringEscapes(value: string): string {
  return value
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\");
}

function extractWriteProseViaRegex(text: string, preserveProse = false): string | null {
  for (const key of SINGLE_PROSE_JSON_KEYS) {
    const keyMarker = `"${key}"`;
    const keyIdx = text.indexOf(keyMarker);
    if (keyIdx < 0) continue;
    const afterKey = text.slice(keyIdx + keyMarker.length);
    const colonMatch = afterKey.match(/^\s*:\s*/);
    if (!colonMatch) continue;
    let rest = afterKey.slice(colonMatch[0].length);
    if (!rest.startsWith('"')) continue;
    rest = rest.slice(1);
    let out = "";
    for (let i = 0; i < rest.length; i += 1) {
      const ch = rest[i]!;
      if (ch === "\\") {
        const next = rest[i + 1];
        if (next == null) break;
        out += ch + next;
        i += 1;
        continue;
      }
      if (ch === '"') {
        const decoded = decodeJsonStringEscapes(out).trim();
        if (decoded.length >= 8) return preserveProse ? decoded : stripTrailingChangeSummaryForWriteContract(decoded);
        break;
      }
      out += ch;
    }
    const truncated = decodeJsonStringEscapes(rest).trim();
    if (truncated.length >= 8) return preserveProse ? truncated : stripTrailingChangeSummaryForWriteContract(truncated);
  }
  return null;
}

function proseFromParsedWriteRecord(
  record: Record<string, unknown>,
  numOptions: number,
  insertion?: { preserveProse?: boolean; optionIndex?: number },
): string | null {
  if (insertion?.preserveProse) {
    if (Array.isArray(record.options)) {
      const index = insertion.optionIndex ?? 1;
      const matches = record.options.filter((entry) => entry && typeof entry === "object" && entry.index === index);
      return matches.length === 1 && typeof matches[0].prose === "string" ? matches[0].prose : null;
    }
    for (const key of SINGLE_PROSE_JSON_KEYS) {
      if (typeof record[key] === "string") return record[key] as string;
    }
    return null;
  }
  if (Number.isFinite(numOptions) && numOptions >= 2) {
    const options = Array.isArray(record.options) ? record.options : null;
    if (!options || options.length < 1) return null;
    const bodies = options
      .map((entry) => {
        if (!entry || typeof entry !== "object") return "";
        const prose = (entry as { prose?: unknown }).prose;
        return typeof prose === "string" ? prose.trim() : "";
      })
      .filter((body) => body.length >= 8);
    if (bodies.length < 1) return null;
    if (bodies.length === 1) return stripTrailingChangeSummaryForWriteContract(bodies[0]!);
    return bodies
      .map((body, index) => `## Option ${index + 1}\n\n${stripTrailingChangeSummaryForWriteContract(body)}`)
      .join("\n\n");
  }

  for (const key of SINGLE_PROSE_JSON_KEYS) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate.trim().length >= 8) {
      return stripTrailingChangeSummaryForWriteContract(candidate.trim());
    }
  }
  return null;
}

/**
 * Extract manuscript prose from a structured Write JSON envelope even when the full
 * contract rejects it. Prevents displaying/inserting the raw
 * `{"replacement_prose":"..."}` string.
 */
export function unwrapWriteProseFromPossiblyStructuredRaw(
  raw: string,
  numOptions: number = 1,
  insertion?: { preserveProse?: boolean; optionIndex?: number },
): string | null {
  let text = String(raw ?? "").trim();
  if (!text) return null;

  for (let depth = 0; depth < 2; depth += 1) {
    if (!(text.startsWith('"') && text.endsWith('"'))) break;
    try {
      const once = JSON.parse(text);
      if (typeof once !== "string" || !once.trim()) break;
      text = once.trim();
    } catch {
      break;
    }
  }

  text = normalizeWriteProseStructuredRaw(text);
  if (!text) return null;

  if (!(text.startsWith("{") && (WRITE_PROSE_JSON_KEY_RE.test(text) || (insertion?.preserveProse && /"options"\s*:/.test(text))))) return null;

  if (text.endsWith("}")) {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const fromParsed = proseFromParsedWriteRecord(
          parsed as Record<string, unknown>,
          numOptions,
          insertion,
        );
        if (fromParsed !== null) return fromParsed;
        if (insertion?.preserveProse) return null;
      }
    } catch {
      // Invalid JSON — regex salvage below.
    }
  }

  // A partial options envelope cannot safely identify the selected option.
  if (insertion?.preserveProse && /"options"\s*:/.test(text)) return null;
  return extractWriteProseViaRegex(text, insertion?.preserveProse);
}
